<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import type { ShopfloorDailyOutputRow } from '../types/shopfloor'

const { t } = useI18n()
const { fetchDailyOutput } = useShopfloorApi()

const reportDate = ref(new Date().toLocaleDateString('en-CA'))
const rows = ref<ShopfloorDailyOutputRow[]>([])
const loading = ref(true)
const error = ref('')
const stageFilter = ref('')
const employeeFilter = ref('')

async function load() {
  loading.value = true
  error.value = ''
  try {
    const output = await fetchDailyOutput(reportDate.value)
    rows.value = output.rows
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

watch(reportDate, load)
onMounted(load)

const stageOptions = computed(() =>
  Array.from(new Set(rows.value.map((row) => row.stage_code))).sort(),
)
const employeeOptions = computed(() =>
  Array.from(
    new Map(
      rows.value
        .filter((row) => row.employee_id != null && row.employee_name)
        .map((row) => [row.employee_id, row.employee_name]),
    ).entries(),
  ).map(([id, name]) => ({ id, name })),
)

const filteredRows = computed(() =>
  rows.value.filter(
    (row) =>
      (!stageFilter.value || row.stage_code === stageFilter.value) &&
      (!employeeFilter.value || String(row.employee_id ?? '') === employeeFilter.value),
  ),
)

const totals = computed(() =>
  filteredRows.value.reduce(
    (acc, row) => ({
      completed: acc.completed + Number(row.completed_quantity || 0),
      rejected: acc.rejected + Number(row.rejected_quantity || 0),
      rework: acc.rework + Number(row.rework_quantity || 0),
    }),
    { completed: 0, rejected: 0, rework: 0 },
  ),
)

function buildCsv(): string {
  const header = ['Stage', 'Stage code', 'Employee', 'Completed', 'Rejected', 'Rework']
  const body = filteredRows.value.map((row) => [
    row.stage_name,
    row.stage_code,
    row.employee_name ?? 'Unassigned',
    String(row.completed_quantity),
    String(row.rejected_quantity),
    String(row.rework_quantity),
  ])
  const lines = [header, ...body].map((cells) => cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
  return lines.join('\r\n')
}

function exportCsv() {
  const blob = new Blob([buildCsv()], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `daily-output-${reportDate.value}.csv`
  link.click()
  URL.revokeObjectURL(url)
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('shopfloor.output.eyebrow') }}</p>
      <h2>{{ t('shopfloor.output.title') }}</h2>
      <p>{{ t('shopfloor.output.description') }}</p>
    </div>
  </div>

  <!-- Filters -->
  <div class="filter-bar">
    <label class="date-field">
      <span>{{ t('shopfloor.output.date') }}</span>
      <input v-model="reportDate" type="date" class="filter-select" />
    </label>
    <select v-model="stageFilter" class="filter-select">
      <option value="">{{ t('shopfloor.output.allStages') }}</option>
      <option v-for="stage in stageOptions" :key="stage" :value="stage">{{ stage }}</option>
    </select>
    <select v-model="employeeFilter" class="filter-select">
      <option value="">{{ t('shopfloor.output.allEmployees') }}</option>
      <option v-for="employee in employeeOptions" :key="employee.id" :value="String(employee.id)">
        {{ employee.name }}
      </option>
    </select>
    <button class="btn-secondary" :disabled="filteredRows.length === 0" @click="exportCsv">{{ t('shopfloor.output.exportCsv') }}</button>
  </div>

  <!-- Totals -->
  <div v-if="!loading && !error && filteredRows.length" class="summary-row">
    <div class="summary-card">
      <span class="summary-value good">{{ totals.completed.toLocaleString() }}</span>
      <span class="summary-label">{{ t('shopfloor.output.completed') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value warn">{{ totals.rejected.toLocaleString() }}</span>
      <span class="summary-label">{{ t('shopfloor.output.rejected') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value warn">{{ totals.rework.toLocaleString() }}</span>
      <span class="summary-label">{{ t('shopfloor.output.rework') }}</span>
    </div>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('shopfloor.output.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('shopfloor.output.retry') }}</button>
  </div>

  <!-- Empty -->
  <div v-else-if="filteredRows.length === 0" class="state-message">
    <p>{{ t('shopfloor.output.empty') }}</p>
  </div>

  <!-- Table -->
  <div v-else class="table-scroll">
  <table class="data-table">
    <thead>
      <tr>
        <th>{{ t('shopfloor.output.stage') }}</th>
        <th>{{ t('shopfloor.output.stageCode') }}</th>
        <th>{{ t('shopfloor.output.employee') }}</th>
        <th>{{ t('shopfloor.output.completed') }}</th>
        <th>{{ t('shopfloor.output.rejected') }}</th>
        <th>{{ t('shopfloor.output.rework') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(row, index) in filteredRows" :key="`${row.stage_code}-${row.employee_id}-${index}`">
        <td>{{ row.stage_name }}</td>
        <td class="cell-code">{{ row.stage_code }}</td>
        <td>{{ row.employee_name ?? t('shopfloor.output.unassigned') }}</td>
        <td class="cell-qty">{{ row.completed_quantity.toLocaleString() }}</td>
        <td class="cell-qty warn">{{ row.rejected_quantity.toLocaleString() }}</td>
        <td class="cell-qty warn">{{ row.rework_quantity.toLocaleString() }}</td>
      </tr>
    </tbody>
  </table>
  </div>
</template>

<style scoped>
.page-heading {
  margin-bottom: 20px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }

.filter-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  align-items: flex-end;
}
.date-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
}
.filter-select {
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  min-width: 180px;
}
.btn-secondary {
  padding: 8px 20px;
  background: #fff;
  color: #27705d;
  border: 1px solid #27705d;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-secondary:disabled { opacity: 0.5; cursor: default; }
.btn-secondary:hover:not(:disabled) { background: #f0f5f3; }
.btn-primary {
  display: inline-block;
  margin-top: 12px;
  padding: 8px 20px;
  background: #27705d;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:hover { background: #1f5c4b; }

.summary-row {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 16px 24px;
  text-align: center;
}
.summary-value { display: block; font-size: 26px; font-weight: 800; color: #1a2b26; }
.summary-value.good { color: #1f5c4b; }
.summary-value.warn { color: #b3472d; }
.summary-label { display: block; font-size: 11px; color: #65736e; text-transform: uppercase; font-weight: 600; margin-top: 2px; }

.state-message {
  padding: 40px 0;
  text-align: center;
  color: #5f6b6a;
}
.state-message.error { color: #b33a3a; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  overflow: hidden;
}
.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }
.cell-code { font-weight: 700; font-family: monospace; }
.cell-qty { font-weight: 600; }
.warn { color: #b3472d; }
</style>
